import { Helmet } from 'react-helmet';
import { useIntl } from 'react-intl';
import { Divider, Form, Skeleton } from 'antd';
import { SaveButton, DefaultRouteTabs } from '@/components/_common';
import { PageProps } from '@/types/PageProps';
import { InvoiceGeneralFormValues } from '@/core/domain/invoice/invoice/Invoice.types';
import { useEffect } from 'react';
import { useInvoiceFormContext } from '@/components/Fiscal/Invoice/InvoiceFormProvider';
import moment from 'moment';
import { useLocation } from 'react-router-dom';
import InvoiceButtonsRepeated from '../InvoiceButtonsRepeated';
import { InvoiceFormTabs } from '@/components/Fiscal/Invoice/InvoiceForms';
import invoiceValidateRoutes from '../../Invoice.validateRoutes';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import InvoiceDeleteButton from '@/components/_common/InvoiceDeleteButton';

const InvoiceEditGeneralPage: React.FC<PageProps> = ({ route }) => {
  const {
    instanceForms,
    invoiceId,
    disabled,
    disabledFields,
    handleSubmit,
    isLoading,
    forms,
    invoice: { data: invoice, isLoading: invoiceIsLoading, dataUpdatedAt },
  } = useInvoiceFormContext();

  const { pathname } = useLocation();
  const URL = '/' + pathname.split('/').slice(1, -1).join('/');

  const { parent } = route.parent;

  const intl = useIntl();

  const [form] = Form.useForm<InvoiceGeneralFormValues>(
    instanceForms.invoiceGeneral,
  );

  useEffect(() => {
    if (invoice && forms.invoiceGeneral) {
      form.setFieldsValue({
        key: invoice?.key,
        numberRps: invoice?.numberRps,
        protocol: invoice?.protocol,
        cancellationProtocol: invoice?.cancellationProtocol,
      });
    }
  }, [dataUpdatedAt]);

  useEffect(() => {
    if (invoice && !forms.invoiceGeneral) {
      form.setFieldsValue({
        ...invoice,
        serie: invoice.serie,
        situation: invoice.situation,
        //----------------------------------------
        documentType: invoice.documentType,
        emissionGoal: invoice.emissionGoal,
        taxpayerType: invoice.taxpayerType,
        operationDestination: invoice.operationDestination,
        taxRegime: invoice.taxRegime,
        faceToFace:
          typeof invoice?.faceToFace === 'boolean'
            ? String(invoice.faceToFace)
            : undefined,
        //----------------------------------------
        finalConsumer:
          typeof invoice?.finalConsumer === 'boolean'
            ? String(invoice.finalConsumer)
            : undefined,
        number: invoice.number,
        emissionDate: invoice.emissionDate
          ? moment(invoice.emissionDate)
          : undefined,
        departureDate: invoice.departureDate
          ? moment(invoice.departureDate)
          : undefined,
        cityId: invoice.cityId,
        intermediaryIndication:
          typeof invoice.intermediaryIndication === 'number'
            ? String(Boolean(invoice.intermediaryIndication))
            : undefined,
        culturalIncentivator:
          invoice.culturalIncentivator?.toString() ?? undefined,
        //------------------------------------
        simpleNational: invoice?.simpleNational?.toString(),
        issWithheld: invoice?.issWithheld?.toString(),
        deductIss: invoice?.deductIss?.toString(),
        pisCofinsWithholding: invoice?.pisCofinsWithholding?.toString(),
        // Tax reform ------------------------
        deliveryPrevisionDate: invoice.deliveryPrevisionDate
          ? moment(invoice.deliveryPrevisionDate)
          : undefined,
      });
    }
  }, [invoice, forms.invoiceGeneral]);

  return (
    <DefaultRouteTabs
      route={invoiceValidateRoutes(route, invoice?.situation)}
      baseKey={invoiceId}
      permissions={route.permissions}
      basePath={URL}
      resourceNotFound={!invoice && !invoiceIsLoading}
      extra={
        <SpaceForButton>
          <InvoiceDeleteButton />

          <SaveButton
            block
            type="primary"
            disabled={disabled || disabledFields}
            onClick={handleSubmit}
            loading={isLoading}
          >
            {intl.formatMessage({ id: 'commons.entity.save' })}
          </SaveButton>
        </SpaceForButton>
      }
    >
      <Helmet title={intl.formatMessage({ id: route.name })} />

      <Skeleton loading={invoiceIsLoading}>
        <Form form={form} name="invoiceGeneral">
          <InvoiceFormTabs
            form={form}
            tabTitle={parent.key}
            invoiceData={invoice}
          />
        </Form>

        <Divider />

        <InvoiceButtonsRepeated />
      </Skeleton>
    </DefaultRouteTabs>
  );
};

export default InvoiceEditGeneralPage;
